/**
 * Preloader utility for SPA performance optimization
 * Preloads critical assets like animations, images, and other resources
 */

type PreloadableAsset = {
  url: string;
  type: 'json' | 'image' | 'font';
  priority?: 'high' | 'low';
};

function createAssetPreloader() {
  const cache = new Map<string, unknown>();
  const loadingPromises = new Map<string, Promise<unknown>>();

  /**
   * Preload a JSON asset (like Lottie animations)
   */
  const preloadJson = async (url: string): Promise<unknown> => {
    const resolvedUrl = url.startsWith('http') ? url : new URL(url, window.location.origin).href;
    if (cache.has(url)) {
      return cache.get(url);
    }

    if (loadingPromises.has(url)) {
      return loadingPromises.get(url);
    }

    const loadPromise = fetch(resolvedUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to preload ${resolvedUrl}: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        cache.set(url, data);
        loadingPromises.delete(url);
        return data;
      })
      .catch((error) => {
        loadingPromises.delete(url);
        console.warn(`Failed to preload asset: ${resolvedUrl}`, error);
        throw error;
      });

    loadingPromises.set(url, loadPromise);
    return loadPromise;
  };

  /**
   * Preload an image
   */
  const preloadImage = async (url: string): Promise<HTMLImageElement> => {
    if (cache.has(url)) {
      return cache.get(url) as HTMLImageElement;
    }

    if (loadingPromises.has(url)) {
      return loadingPromises.get(url) as Promise<HTMLImageElement>;
    }

    const loadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        cache.set(url, img);
        loadingPromises.delete(url);
        resolve(img);
      };
      img.onerror = () => {
        loadingPromises.delete(url);
        reject(new Error(`Failed to preload image: ${url}`));
      };
      img.src = url;
    });

    loadingPromises.set(url, loadPromise);
    return loadPromise;
  };

  /**
   * Preload multiple assets
   */
  const preloadAssets = async (assets: PreloadableAsset[]): Promise<void> => {
    const highPriorityAssets = assets.filter((asset) => asset.priority === 'high');
    const lowPriorityAssets = assets.filter((asset) => asset.priority !== 'high');

    // Load high priority assets first
    if (highPriorityAssets.length > 0) {
      await Promise.allSettled(
        highPriorityAssets.map((asset) => {
          switch (asset.type) {
            case 'json':
              return preloadJson(asset.url);
            case 'image':
              return preloadImage(asset.url);
            default:
              return Promise.resolve();
          }
        }),
      );
    }

    // Load low priority assets in background
    if (lowPriorityAssets.length > 0) {
      // Don't await these - let them load in background
      Promise.allSettled(
        lowPriorityAssets.map((asset) => {
          switch (asset.type) {
            case 'json':
              return preloadJson(asset.url);
            case 'image':
              return preloadImage(asset.url);
            default:
              return Promise.resolve();
          }
        }),
      ).catch(() => {
        // Silently handle background loading errors
      });
    }
  };

  /**
   * Get cached asset
   */
  const getCached = (url: string): unknown | null => {
    return cache.get(url) || null;
  };

  /**
   * Check if asset is cached
   */
  const isCached = (url: string): boolean => {
    return cache.has(url);
  };

  /**
   * Clear cache
   */
  const clearCache = (): void => {
    cache.clear();
    loadingPromises.clear();
  };

  return {
    preloadJson,
    preloadImage,
    preloadAssets,
    getCached,
    isCached,
    clearCache,
  };
}

// Export singleton instance
export const assetPreloader = createAssetPreloader();

/**
 * Preload critical app assets
 * Call this early in your app initialization
 */
export const preloadCriticalAssets = async (): Promise<void> => {
  const criticalAssets: PreloadableAsset[] = [
    {
      url: '/S0_Logo.json',
      type: 'json',
      priority: 'high',
    },
    {
      url: '/S0_Logo_dark.json',
      type: 'json',
      priority: 'high',
    },
  ];

  await assetPreloader.preloadAssets(criticalAssets);
};
